/**
 * EPIC PLAT strings, English.
 *
 * Allowed to be incomplete: a missing key falls back to Arabic, which keeps a
 * partially translated build usable rather than showing raw keys.
 */
export const platformEn: Record<string, string> = {
  'platform.tenants.title': 'Subscribed clinics',
  'platform.tenants.subtitle':
    '{count} tenants · {expiring} trials ending this week · no clinical patient data is shown',
  'platform.tenants.filter.plan': 'Plan',
  'platform.tenants.filter.status': 'Status',
  'platform.tenants.filter.all': 'All',
  'platform.tenants.filter.overLimit': 'Over limit only',
  'platform.tenants.search': 'Search by clinic name or slug',
  'platform.tenants.col.clinic': 'Clinic',
  'platform.tenants.col.plan': 'Plan',
  'platform.tenants.col.status': 'Status',
  'platform.tenants.col.users': 'Users',
  'platform.tenants.col.patients': 'Patients',
  'platform.tenants.col.storage': 'Storage',
  'platform.tenants.col.lastActivity': 'Last activity',
  'platform.tenants.empty': 'No matching clinics',
  'platform.tenants.emptyBody': 'Try widening the search or clearing the filters.',
  'platform.tenants.noActivity': 'No activity',
  'platform.tenants.overLimit': 'Over limit',

  'platform.kpi.active': 'Active tenants',
  'platform.kpi.trial': 'Trials running',
  'platform.kpi.grace': 'In grace',
  'platform.kpi.suspended': 'Suspended',
  'platform.kpi.mrr': 'Monthly recurring revenue',

  'platform.tenant.since': 'Since {date}',
  'platform.tenant.changePlan': 'Change plan',
  'platform.tenant.requestSupport': 'Request support session',
  'platform.tenant.notice':
    'A support session requires a written purpose, a fixed duration and re-authentication. It shows a persistent banner inside the app, and every action taken during it is audited. Clinical patient detail is never shown outside an explicit support context.',
  'platform.tenant.usage.ACTIVE_USERS': 'Active users',
  'platform.tenant.usage.ACTIVE_PATIENTS': 'Active patients',
  'platform.tenant.usage.BRANCHES': 'Branches',
  'platform.tenant.usage.STORAGE_BYTES': 'Storage',
  'platform.tenant.usage.MESSAGES_SENT': 'Messages this month',
  'platform.tenant.usage.AI_OPERATIONS': 'AI operations',
  'platform.tenant.operations': 'Operational metrics (non-clinical)',
  'platform.tenant.ops.appointments': 'Appointments, last 30 days',
  'platform.tenant.ops.visits': 'Finalised visits',
  'platform.tenant.ops.invoices': 'Settled invoices',
  'platform.tenant.ops.patients': 'Active patients',
  'platform.tenant.ops.branches': 'Active branches',
  'platform.tenant.support': 'Recent support actions',
  'platform.tenant.support.empty': 'No support actions recorded',
  'platform.tenant.staff': 'Clinic team',
  'platform.tenant.staff.never': 'Never signed in',
  'platform.tenant.notFound': 'No clinic with that id',
  'platform.tenant.back': 'Back to the list',
  'platform.tenant.noSubscription': 'This clinic has no subscription record',
  'platform.tenant.unlimited': 'Unlimited',
  'platform.tenant.overrideReason': 'Last override reason: {reason}',

  'platform.impersonate.title': 'Request a support session',
  'platform.impersonate.tenant': 'Clinic',
  'platform.impersonate.purpose': 'Purpose',
  'platform.impersonate.purposeHint':
    'Give the ticket number or the reason for entering. This text is stored in the audit trail and is visible to the clinic.',
  'platform.impersonate.purposePlaceholder': 'e.g. Billing ticket 4102 — invoice will not issue',
  'platform.impersonate.duration': 'Duration',
  'platform.impersonate.durationHint': 'The session expires on the server when the time is up.',
  'platform.impersonate.minutes': '{count} minutes',
  'platform.impersonate.audited':
    'Every action you take during this session is recorded against your name and the clinic. Access is limited to operational reads — clinical records and financial transactions are not shown.',
  'platform.impersonate.submit': 'Start session',
  'platform.impersonate.cancel': 'Cancel',
  'platform.impersonate.needsPurpose': 'Write a clear purpose (at least 8 characters)',
  'platform.impersonate.reauth':
    'This is a sensitive action. Re-confirm your password from the account screen, then try again.',
  'platform.impersonate.alreadyActive':
    'You already have a support session open. End it before starting another.',
  'platform.impersonate.started': 'Session started. Reload to work inside the clinic.',
  'platform.impersonate.active': 'Session open',
  'platform.impersonate.endSession': 'End session',

  'platform.change.title': 'Change plan',
  'platform.change.plan': 'New plan',
  'platform.change.reason': 'Reason',
  'platform.change.reasonHint': 'Mandatory. Stored in the audit trail and shown to the clinic.',
  'platform.change.submit': 'Apply change',
  'platform.change.needsReason': 'Write a clear reason (at least 8 characters)',

  'platform.health.title': 'Platform health',
  'platform.health.subtitle':
    'Optional dependencies are isolated — an outage there does not stop patient care',
  'platform.health.required': 'Required',
  'platform.health.optional': 'Optional',
  'platform.health.state.up': 'Up',
  'platform.health.state.degraded': 'Degraded',
  'platform.health.state.down': 'Down',
  'platform.health.state.not_configured': 'Not configured',
  'platform.health.jobs': 'Background jobs',
  'platform.health.jobs.pending': 'Pending',
  'platform.health.jobs.retrying': 'Retrying',
  'platform.health.jobs.dead': 'Dead-lettered (DLQ)',
  'platform.health.jobs.lag': 'Queue lag',
  'platform.health.alerts': 'Active operational alerts',
  'platform.health.alerts.empty': 'No active operational alerts',
  'platform.health.checkedAt': 'Last checked: {time}',

  'platform.alerts.title': 'Subscription alerts',
  'platform.alerts.TRIAL_EXPIRING': 'Trial ending soon',
  'platform.alerts.TRIAL_EXPIRED': 'Trial expired',
  'platform.alerts.GRACE': 'In grace',
  'platform.alerts.SUSPENDED': 'Suspended',
  'platform.alerts.OVER_LIMIT': 'Over plan limits',
  'platform.alerts.empty': 'No alerts — every subscription is within its limits',
  'platform.alerts.daysLeft': '{days} days left',
  'platform.alerts.daysAgo': '{days} days ago',
  'platform.alerts.today': 'Today',

  'platform.subStatus.TRIAL': 'Trial',
  'platform.subStatus.ACTIVE': 'Active',
  'platform.subStatus.GRACE': 'Grace',
  'platform.subStatus.SUSPENDED': 'Suspended',
  'platform.subStatus.CANCELLED': 'Cancelled',
  'platform.subStatus.EXPIRED': 'Expired',

  'platform.todo.title': 'This screen is out of scope for this stage',
  'platform.todo.body':
    'Platform settings, activation analytics, the diagnostics package and repair commands are outside the current PLAT scope. See the pull request for what was and was not built.',
};
